import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
import { ApplicantInfoStoreService } from "./applicant-info-store.service";
import {
  AppLoadError,
  AppSubmitError,
  IAPIResponseFile,
  IfilesInStore,
  IUploadResponse,
} from "../shared/models";
import { UploadService } from "../shared/services/api/upload.service";
import {
  getUnUploadedDocumentsFromStorage,
  setUnUploadedDocumentsToStorage,
} from "../utils";
import { SharedStoreService } from "./shared-store.service";
import { GtmService } from "../shared/services/gtm.service";

@Injectable({
  providedIn: "root",
})
export class UploadStoreService {
  private _files = new BehaviorSubject<IfilesInStore | null>(null);
  private _loading = new BehaviorSubject<boolean>(false);
  private _error = new BehaviorSubject<AppLoadError | AppSubmitError | null>(
    null
  );
  private _unUploadedForms = new BehaviorSubject<string[]>(
    getUnUploadedDocumentsFromStorage()
  );

  files$ = this._files.asObservable();
  loading$ = this._loading.asObservable();
  error$ = this._error.asObservable();
  unUploadedForms$ = this._unUploadedForms.asObservable();

  get files() {
    return this._files.getValue();
  }

  get unUploadedForms() {
    return this._unUploadedForms.getValue();
  }

  constructor(
    private uploadService: UploadService,
    private applicantInfoStore: ApplicantInfoStoreService,
    private sharedStore: SharedStoreService,
    private gtmService: GtmService
  ) {}

  selectUnUploadedForms() {
    return this.unUploadedForms$.pipe(map((files) => files));
  }

  selectFiles(): Observable<IfilesInStore> {
    return this.files$.pipe(map((files) => files));
  }

  // Select the loading state
  selectLoading(): Observable<boolean> {
    return this.loading$.pipe(map((loading) => loading));
  }

  // Select the error state
  selectError(): Observable<AppLoadError | null> {
    return this.error$.pipe(map((error) => error));
  }

  selectUploadReview() {
    return this.files$.pipe(
      map((filesList) => {
        if (filesList) {
          return filesList.files.reverse();
        }
        return null;
      })
    );
  }

  /* getFiles() is responsible for fetching files from
the server using the uploadService.getFiles() method. */
  getFiles() {
    this._loading.next(true);
    this._error.next(null);

    const appId = this.applicantInfoStore.applicantInfo.appId;

    if (!appId) {
      this._error.next(new AppLoadError("0", "Application ID is missing"));
      this._loading.next(false);
      return;
    }

    return this.uploadService.getFiles().pipe(
      map((filesResponse: IAPIResponseFile[]) => {
        const toStore: IfilesInStore = {
          id: appId,
          files: [],
        };

        for (let i = 0; i < filesResponse.length; i++) {
          toStore.files.push({
            docId: filesResponse[i].Document_Id,
            fileName: filesResponse[i].Document_Name,
            type: filesResponse[i].Document_Type,
          });
        }

        //Remove index for 'other' docId that makes it unique in DB
        for (let i = 0; i < toStore.files.length; i++) {
          if (toStore.files[i].docId.indexOf("other") === 0) {
            toStore.files[i].docId = "other";
          }
        }

        this._files.next(toStore);
        this._loading.next(false);

        return toStore;
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err instanceof HttpErrorResponse ? err.error : err;
        const displayError = error
          ? new AppLoadError(error.id, error.message)
          : new AppLoadError();
        this._error.next(displayError);
        this._loading.next(false);
        throw err;
      })
    );
  }

  /**
   * The function handles uploading files, updating loading and error states, and
   * triggering various actions upon completion or error.
   * @param {FormData} payload
   * @returns returns an Observable that emits the response from the uploadService.uploadFiles.
   * The response is then mapped to perform some actions like resetting
   * form, updating loading state, pushing a tag using gtmService, triggering navigation, and finally
   * returning the response. If an error occurs during the upload process, it catches the error, creates
   * an AppSubmitError
   */
  uploadFiles(payload: FormData) {
    this._loading.next(true);
    this._error.next(null);

    return this.uploadService.uploadFiles(payload).pipe(
      map((response: IUploadResponse) => {
        this.resetUnUploadedForm();
        this._loading.next(false);
        this.gtmService.pushTag({
          event: "international_application_progress",
          step_number: "6",
          step_name: "file-up",
        });

        this.sharedStore.triggerNavigationAfterSubmit("DOCUMENT_UPLOAD");
        return response;
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err instanceof HttpErrorResponse ? err.error : err;
        const displayError = error
          ? new AppSubmitError(error.id, error.message)
          : new AppSubmitError();
        this._error.next(displayError);
        this._loading.next(false);
        throw err;
      })
    );
  }

  // Update the unUploadedForms that are required based on the changes in the last step
  setUnUploadedForm(formId: string): void {
    const currentForms = this.unUploadedForms;
    this._unUploadedForms.next([...currentForms, formId]);
    setUnUploadedDocumentsToStorage([...currentForms, formId]);
  }

  resetUnUploadedForm() {
    this._unUploadedForms.next(getUnUploadedDocumentsFromStorage());
  }

  clearError(): void {
    this._error.next(null);
  }
}
